import {
  ChakraFlex,
  ChakraGrid,
  FAIconName,
  FireflyIconButton,
  Icon,
  TagDropdown,
  TagProps,
  Text,
} from '@equip.health/ui';

import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { SessionGuideEvents } from '~/analytics/sessionGuideEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AnalyticsEvent } from '~/analytics';
import {
  CarePlanNoteManifestationResponse,
  CarePlanNoteProblemResponse,
} from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import {
  buildGoalStatusTagDropdownProps,
  toCarePlanNoteGoalTagDropdownOptions,
} from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalTagDropdownOptions';
import { PatientNoteManifestationFrequencyRow } from '~/components/maud/patients/note/PatientNoteManifestationFrequencyRow';
import { useGetProblemTypesGoalStatusesMapQuery } from '~/store/services/refCarePlanEnums';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { useNoteGoalDescription } from '~/lib/hooks/carePlan/useNoteGoalDescription';
import { PatientNoteProblemFrequencyRow } from './PatientNoteProblemFrequencyRow';
import { PatientNoteGoalDescriptionPanel } from './PatientNoteGoalDescriptionPanel';
import { PatientNoteGoalStatusProviderAvatar } from './PatientNoteGoalStatusProviderAvatar';

type Props = {
  noteExternalId: string;
  problem: CarePlanNoteProblemResponse;
  showFrequencyErrors: boolean;
  treatmentEpisodeId: string;
};

export const PatientNoteGoalCard = ({
  noteExternalId,
  problem,
  showFrequencyErrors,
  treatmentEpisodeId,
}: Props) => {
  const goal = problem.goals[0];
  const { trackEvent } = useAnalytics();
  const { carePlanM4MedPsychProblems: isMedPsychProblemsEnabled } =
    useFlags<LDFlags>();

  const { data: problemTypesGoalStatusesMap } =
    useGetProblemTypesGoalStatusesMapQuery();

  const sendAnalyticsEvent = (analyticsEvent: AnalyticsEvent) => {
    trackEvent(analyticsEvent, {
      Source: AnalyticsSourceValues.SESSION_GUIDE,
    });
  };

  const {
    description,
    isDescriptionChangedAndVisible,
    handleRevert,
    handleStatusChange,
    isReverting,
    isUpdateGoalLoading,
    isUpdateGoalSuccess,
    onGoalDescriptionBlur,
    setDescription,
    shouldShowDescriptionBox,
  } = useNoteGoalDescription({
    goal,
    noteExternalId,
    onBeforeStatusChange: () =>
      sendAnalyticsEvent(
        SessionGuideEvents.sessionGuideGoalStatusOptionSelected,
      ),
    treatmentEpisodeId,
  });

  const sortedManifestationsWithFrequencies = useMemo(
    () =>
      problem.manifestations
        .filter((m) => m.frequency != null)
        .toSorted(
          (m1, m2) =>
            m1.pathwayManifestation.sortOrder -
            m2.pathwayManifestation.sortOrder,
        ) as CarePlanNoteManifestationResponse[],
    [problem.manifestations],
  );

  const isNotAssessed = goal.status.code === CarePlanGoalStatus.NOT_ASSESSED;

  const statusDropdownOptions: TagProps[] = useMemo(() => {
    const problemType = problem.problemType ?? ProblemTypeCode.EATING_DISORDER;

    return toCarePlanNoteGoalTagDropdownOptions(
      problemTypesGoalStatusesMap?.[problemType] ?? [],
      isMedPsychProblemsEnabled,
    );
  }, [
    problem.problemType,
    problemTypesGoalStatusesMap,
    isMedPsychProblemsEnabled,
  ]);

  return (
    <ChakraFlex
      bg={isNotAssessed ? 'neutral.background.secondary' : ''}
      borderColor={isNotAssessed ? 'transparent' : 'neutral.line'}
      borderRadius="12px"
      borderWidth={isNotAssessed ? '0' : '1px'}
      direction="column"
      gap="12px"
      padding="18px 18px 12px 18px"
    >
      <ChakraGrid
        alignItems="center"
        columnGap="12px"
        templateColumns="1fr auto auto"
        templateRows="1fr"
        width="100%"
      >
        <Text
          variant="h5"
          data-testid={`patient-note-drawer__goal-title__${goal.id}`}
        >
          {goal.pathwayGoal.label}
        </Text>
        {isNotAssessed ? (
          <>
            <ChakraFlex alignItems="center" gap="6px">
              <Icon
                color="neutral.secondary"
                name={FAIconName.eyeSlashRegular}
                size="md"
              />
              <Text color="neutral.secondary">{goal.status.label}</Text>
            </ChakraFlex>
            <FireflyIconButton
              ariaLabel={`Revert ${goal.status.label.toLowerCase()} status`}
              icon={FAIconName.penRegular}
              id={`note-goal-${goal.id}-revert-button`}
              isDisabled={isReverting}
              isLoading={isReverting}
              onClick={handleRevert}
              size="small"
              variant="tertiary"
            />
          </>
        ) : (
          <>
            <PatientNoteGoalStatusProviderAvatar
              goalId={goal.id}
              noteExternalId={noteExternalId}
              statusHistory={goal.statusHistories?.[0]}
              description={goal.description}
            />
            <TagDropdown
              id={`note-goal-${goal.id}-status-dropdown`}
              menuOptions={statusDropdownOptions}
              {...buildGoalStatusTagDropdownProps(
                goal.status.code,
                goal.status.label,
                statusDropdownOptions,
              )}
              onDropdownOpen={() =>
                sendAnalyticsEvent(
                  SessionGuideEvents.sessionGuideGoalStatusDropdownClicked,
                )
              }
              onOptionClick={handleStatusChange}
            />
          </>
        )}
      </ChakraGrid>
      {!isNotAssessed && shouldShowDescriptionBox && (
        <PatientNoteGoalDescriptionPanel
          description={description}
          isDescriptionChangedAndVisible={isDescriptionChangedAndVisible}
          goalId={goal.id}
          idPrefix="patient-note-goal-card-desc"
          isUpdateGoalLoading={isUpdateGoalLoading}
          isUpdateGoalSuccess={isUpdateGoalSuccess}
          onBlur={() => {
            sendAnalyticsEvent(
              SessionGuideEvents.sessionGuideGoalDescriptionBlurred,
            );
            onGoalDescriptionBlur();
          }}
          onChange={setDescription}
          onFocus={() =>
            sendAnalyticsEvent(
              SessionGuideEvents.sessionGuideGoalDescriptionFocused,
            )
          }
        />
      )}
      {!isNotAssessed &&
        problem.manifestations.length === 0 &&
        problem.frequency && (
          <PatientNoteProblemFrequencyRow
            noteExternalId={noteExternalId}
            problem={problem}
            treatmentEpisodeId={treatmentEpisodeId}
            showFrequencyErrors={showFrequencyErrors}
          />
        )}
      {!isNotAssessed &&
        sortedManifestationsWithFrequencies.map((manifestation) => (
          <PatientNoteManifestationFrequencyRow
            key={`${problem.id}-${manifestation.pathwayManifestation.code}`}
            manifestation={manifestation}
            noteExternalId={noteExternalId}
            problem={problem}
            treatmentEpisodeId={treatmentEpisodeId}
            showFrequencyErrors={showFrequencyErrors}
          />
        ))}
    </ChakraFlex>
  );
};
